/**
 * The round an applicant is mid-registration for, parked so it survives the verification email.
 *
 * ## Why this exists at all
 *
 * The dossier is created once the address is confirmed, and confirmation happens on
 * `/verify-email?token=…` — a link the BACKEND builds
 * (`{IDENTITY_PUBLIC_BASE_URL}/verify-email?token={token}`, `identity/services.py`). That link
 * carries a token and nothing else: no round, and `confirm-link/` returns no round either. So at the
 * moment of confirmation the app knows the applicant is verified and has no idea what they were
 * applying to.
 *
 * The alternatives were all worse:
 * - **Ask the server.** `GET /acquire/applications/` lists the applicant's dossiers — but none
 *   exists yet, which is the whole point. There is nothing to read.
 * - **In-app code entry** would split the flow between the email and an unrelated form. The
 *   verification link already carries the complete proof and works on any device, so there is no
 *   second code to copy or enter.
 * - **Put it in the link.** The backend owns that URL; the frontend cannot add to it.
 *
 * ## What it holds, and why that is safe to store
 *
 * A round UUID and nothing else. Round ids are PUBLIC — they ship in the anonymous catalogue
 * response — so this is not a secret, not personal data, and reveals nothing about who parked it.
 * No email, no name, no token, no session material ever goes in here.
 *
 * ## Its limits, stated rather than discovered
 *
 * `localStorage` is per-browser, so this works when the applicant opens the mail in the same browser
 * they registered from — the common case — and NOT when they open it on their phone. That is a real
 * gap and it degrades honestly: the cross-device applicant is still verified, and returns through
 * the catalogue, where `/apply/{roundId}` creates the dossier from the round id in the URL. Nothing
 * is lost; one extra step is paid.
 *
 * Every access is wrapped: a private window, cleared site data, or a browser set to block storage
 * makes these throw rather than return null, and a verification that succeeded must never be
 * reported as failed because of it.
 */
const KEY = "dzc.pending-round.v1";

/** A round id looks like this or it does not go in. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Park the round an applicant just registered for. Called once, at registration. */
export function setPendingRound(roundId: string): void {
  try {
    if (!UUID.test(roundId)) return;
    window.localStorage.setItem(KEY, roundId);
  } catch {
    // Storage unavailable. The cross-device path below is the fallback, so this is survivable.
  }
}

/**
 * The parked round, or `null`.
 *
 * Validated on the way OUT as well as in: this value is read straight out of a store the user can
 * edit and is then put into a URL and a request body. A stale key from an older shape, or anything
 * hand-edited, must read as "nothing parked" rather than as a round id.
 */
export function getPendingRound(): string | null {
  try {
    const value = window.localStorage.getItem(KEY);
    return value !== null && UUID.test(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * Forget it.
 *
 * Called once the round has been USED — after the dossier exists. Deliberately not cleared at
 * verification: the applicant may verify and close the tab before the redirect lands, and dropping
 * it there would strand the very next visit.
 */
export function clearPendingRound(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // Nothing to do — a stale id is harmless, and it is re-validated on every read.
  }
}
